export const nightscoutDatabaseStorage = `

Your Nightscout site stores glucose readings, treatments, pump data, and device status in a database. Over time — especially when looping with OpenAPS or uploading frequent device status updates — that storage can grow until you hit your hosting limit. 

This guide shows how to **check how much data you are using** and how to **free up space** safely.

## Why database size matters

Nightscout hosting plans include a fixed amount of database storage. Glucose entries and treatments are usually the largest collections, but **device status** data (battery levels, pump sync messages, OpenAPS status updates) can also accumulate quickly. If you exceed your limit, you may see a red percentage on your dashboard — for example **291%** (see above image).

</br>

## Step 1: Enable the Database Size plugin

Before you can see your storage usage on the main screen, you need to turn on the **Database Size** plugin.

1. Open your Nightscout site in a browser.
2. Click the **hamburger menu** (☰) in the top-right corner.
3. Scroll down to **Show Plugins**.
4. Check the box next to **Database Size**.
5. Click on the save button at the bottom to save your changes.

<div style="display: flex; flex-direction: column; align-items: center; margin: 2rem 0;">
  <img
    src="/assets/blogs/nightscout-database-storage/enable-database-size-plugin.png"
    alt="Nightscout settings menu with the Database Size plugin enabled"
    style="max-width: 100%; width: 420px; height: auto; border-radius: 8px; box-shadow: 0 2px 12px rgba(0,0,0,0.12);"
  />
  <p style="text-align: center; font-size: 0.8125rem; line-height: 1.45; color: #6b6b6b; margin: 0.5rem auto 0; max-width: 680px; padding: 0 1rem;">
    Enable <strong>Database Size</strong> under Show Plugins in the settings menu.
  </p>
</div>

## Step 2: Read your storage usage on the dashboard

After enabling the plugin, return to the main Nightscout view. In the status bar at the top you will see a **database icon** with a percentage next to it.

- **Green or normal text** — you are within your storage limit.
- **Red text** — you are over your limit and should clean up data soon.

Hover over or click the indicator for details. You will see something like:

> Data size 1445.32 MiB of 496 MiB (291%)

The first number is how much you are using; the second the standard Nightscout limit. Be aware that some hosting providers offer more storage, and in that case a red number will not always mean you are over the limit.

<div style="display: flex; flex-direction: column; align-items: center; margin: 2rem 0;">
  <img
    src="/assets/blogs/nightscout-database-storage/dashboard-database-size.png"
    alt="Nightscout dashboard showing database storage at 291 percent"
    style="max-width: 100%; width: 720px; height: auto; border-radius: 8px; box-shadow: 0 2px 12px rgba(0,0,0,0.12);"
  />
  <p style="text-align: center; font-size: 0.8125rem; line-height: 1.45; color: #6b6b6b; margin: 0.5rem auto 0; max-width: 680px; padding: 0 1rem;">
    The database indicator in the top bar shows your current usage. Red means you are over the limit.
  </p>
</div>

## Step 3: Remove old data to free up storage

When your database is full, you need to delete data you no longer need or request an increase of your storage limit. 

If you want to increase your storage limit, you can request this via <a href="mailto:info@opensource.clinic">info@opensource.clinic</a>.

For deleting old data, Nightscout provides admin tools.

1. Open the **hamburger menu** (☰) and choose **Admin Tools**, or go directly to \`https://your-nightscout-site.com/admin\`.
2. Log in with your **API secret** if prompted.
3. Scroll to the **Clean Mongo** sections shown below.

From there you can clean three collections:

### Clean device status (status database)

Device status records include uploader battery levels, pump sync info, and OpenAPS messages. This collection often grows the fastest and is usually safe to trim.

### Clean treatments

Treatments include boluses, carbs, notes, and profile changes.

### Clean glucose entries

Entries are your CGM / fingerstick glucose values.


<div style="display: flex; flex-direction: column; align-items: center; margin: 2rem 0;">
  <img
    src="/assets/blogs/nightscout-database-storage/clean-mongo-admin.png"
    alt="Nightscout admin page for cleaning MongoDB collections"
    style="max-width: 100%; width: 720px; height: auto; border-radius: 8px; box-shadow: 0 2px 12px rgba(0,0,0,0.12);"
  />
  <p style="text-align: center; font-size: 0.8125rem; line-height: 1.45; color: #6b6b6b; margin: 0.5rem auto 0; max-width: 680px; padding: 0 1rem;">
  Admin tools let you delete all or old records from device status, treatments, and glucose entries.
  </p>
</div>

### Suggested order when cleaning up

1. Start with **device status** — delete records older than 30 days, or delete all if you only need to fix a stuck battery indicator.
2. If you still need more space, trim **treatments** and **entries** older than 180 days (or a shorter period if you are comfortable losing that history).
3. Return to the main dashboard and check the database size indicator again.

## Important warnings

- **Deleted data cannot be recovered.** Make sure you do not remove glucose or treatment history you still need for your loop, reports, or care team.
- **Do not delete recent entries** if you rely on Nightscout for remote monitoring.
- If you are unsure how far back to keep data, start with device status only — that is usually the safest place to reclaim space.
- After cleaning, wait a minute and refresh the page to see the updated storage percentage.


<!-- blog-contact-footer -->
`;
